import { Box, Link, Typography, useMediaQuery, useTheme } from "@mui/material";
import { GridColDef, GridRowsProp } from "@mui/x-data-grid";
import { t } from "i18next";

import {
  IOrganizationUnit,
  NendaProduct,
} from "../../../../../../types/NendaTypes";
import { getCompanyIdByPremise } from "../../../../../utils/company";

import { useNavigate } from "react-router-dom";
import DefaultDataGrid from "../../../ui-components/dataGrid/DefaultDataGrid";
import { ProductIcon } from "@client/components/CustomerPortal/Pages/Premises";
import { add } from "date-fns";

interface PremiseListProps {
  premises: IOrganizationUnit[];
}

const PremiseList: React.FC<PremiseListProps> = ({ premises }) => {
  const theme = useTheme();

  const rows: GridRowsProp = premises.map((premise) => {
    const premiseWithCreated = premise as IOrganizationUnit & {
      created?: string;
    };

    return {
      id: premiseWithCreated._id,
      name: premise.name,
      address: `${premise.address.streetAddress} ${premise.address.city}`,
      products: premise.nendaProducts.map((product) => product).join(", "),
      category: premise.category,
      company: getCompanyIdByPremise(premise),
      created_at: premiseWithCreated?.createdAt || premiseWithCreated?.created,
    };
  });

  const navigate = useNavigate();

  const columns: GridColDef[] = [
    { field: "id", flex: 1, headerName: "ID" },
    {
      field: "name",
      flex: 1,
      headerName: t("customerportal.pages.premises.columns.name") ?? "Name",
      renderCell: (params) => {
        return (
          <Link
            sx={{ color: theme.palette.primary.dark, cursor: "pointer" }}
            onClick={() => navigate(`/dashboard/premise/${params.row.id}`)}
          >
            {params.value}
          </Link>
        );
      },
    },
    {
      field: "address",
      flex: 1,
      headerName:
        t("customerportal.pages.premises.columns.address") ?? "Address",
      renderCell: (params) => {
        return <Typography variant="body1">{params.value}</Typography>;
      },
    },
    {
      field: "category",
      flex: 1,
      headerName:
        t("customerportal.pages.premises.columns.category") ?? "Category",
      renderCell: (params) => {
        return <Typography variant="body1">{params.value}</Typography>;
      },
    },
    {
      field: "products",
      flex: 1,
      headerName:
        t("customerportal.pages.premises.columns.products") ?? "Products",
      renderCell: (params) => {
        return (
          <Box sx={{ display: "flex", flexDirection: "row" }}>
            {params.value.split(",").map((product: NendaProduct) => (
              <ProductIcon product={product.trim()} key={product.trim()} />
            ))}
          </Box>
        );
      },
    },
  ];

  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  return (
    <Box sx={{ position: "relative" }}>
      <DefaultDataGrid
        rows={rows}
        columns={columns}
        sortingConfig={[{ field: "created_at", sort: "desc" }]}
        visibleColumns={{
          id: false,
          products: isMobile ? false : true,
          category: isMobile ? false : true,
          company: isMobile ? false : true,
          created_at: isMobile ? false : true,
        }}
      />
    </Box>
  );
};

export default PremiseList;
